// Documento, celular y correo del cliente — SPEC_FORMULARIO_CLIENTE_v0.1.md
// RN-FC1 a RN-FC11, PLAN v0.1 D1. Puro.
//
// ESPEJO de pos-graciela-api/src/domain/documentoCliente.js: misma lógica y
// mismos vectores (test/fixtures/documentoCliente.vectores.json, copia exacta).
// Si una regla cambia allá, cambia acá. El API es quien decide; esto evita
// mandar lo que va a rechazar y le dice al operador por qué.

export const TIPOS = Object.freeze({
    CI: { id: 1, nombre: 'Cédula' },
    RUC: { id: 2, nombre: 'RUC' },
    PASAPORTE: { id: 3, nombre: 'Pasaporte' },
    CEDULA_EXTRANJERA: { id: 4, nombre: 'Cédula extranjera' },
    CARNET_RESIDENCIA: { id: 5, nombre: 'Carnet de residencia' }
});
export const TIPO_POR_ID = Object.freeze(Object.fromEntries(Object.entries(TIPOS).map(([codigo, t]) => [t.id, codigo])));

const limpiar = (v) => String(v ?? '').trim().replace(/[.\s]/g, '');

/**
 * calcularDvRuc — dígito verificador del RUC paraguayo (módulo 11 de la SET).
 * Verificado contra 8 RUC del padrón DNIT el 2026-10-11.
 */
export const calcularDvRuc = (base) => {
    let k = 2;
    let total = 0;
    for (const d of String(base).split('').reverse()) {
        total += Number(d) * k;
        k = k < 11 ? k + 1 : 2;
    }
    const resto = total % 11;
    return resto > 1 ? 11 - resto : 0;
};

/** validarRuc — `base-dv` o `base` sola (completa el DV). */
export const validarRuc = (texto) => {
    const t = limpiar(texto);
    const m = /^(\d{3,9})(?:-(\d))?$/.exec(t);
    if (!m) return { ok: false, error: 'El RUC tiene que ser el número y el dígito verificador, por ejemplo 80001324-7.' };
    const dv = calcularDvRuc(m[1]);
    if (m[2] != null && Number(m[2]) !== dv) {
        return { ok: false, error: `El dígito verificador no corresponde: para ${m[1]} es ${dv}.` };
    }
    return { ok: true, base: m[1], ruc: `${m[1]}-${dv}` };
};

/**
 * validarDocumento — RN-FC2 a RN-FC4.
 * @returns { ok, tipo, numero, ruc?, error? } con el número ya normalizado.
 */
export const validarDocumento = ({ tipo, numero } = {}) => {
    const codigo = String(tipo || '').toUpperCase();
    if (!TIPOS[codigo]) return { ok: false, error: 'Elegí el tipo de documento.' };
    const n = limpiar(numero);
    if (!n) return { ok: false, error: 'Ingresá el número de documento.' };
    if (codigo === 'CI') {
        if (!/^\d{4,9}$/.test(n)) return { ok: false, error: 'La cédula son sólo dígitos (entre 4 y 9), sin dígito verificador.' };
        return { ok: true, tipo: codigo, numero: n };
    }
    if (codigo === 'RUC') {
        const r = validarRuc(n);
        return r.ok ? { ok: true, tipo: codigo, numero: r.ruc, ruc: r.ruc } : { ok: false, error: r.error };
    }
    const alfa = n.toUpperCase();
    if (!/^[A-Z0-9-]{4,20}$/.test(alfa)) return { ok: false, error: 'El documento son letras y números, de 4 a 20, sin espacios.' };
    return { ok: true, tipo: codigo, numero: alfa };
};

/**
 * columnasDeCliente — RN-FC10. Las columnas de `clientes` para un documento ya
 * validado. `rucPadron`: el RUC que el padrón le encontró a una cédula.
 */
export const columnasDeCliente = ({ tipo, numero, rucPadron = null, tipoPersona = null }) => {
    const id = TIPOS[tipo].id;
    if (tipo === 'CI') {
        const ruc = rucPadron && validarRuc(rucPadron).ok ? validarRuc(rucPadron).ruc : null;
        return { tipo_documento_id: id, ci: numero, ruc, nro_documento: ruc || numero };
    }
    if (tipo === 'RUC') {
        const base = numero.split('-')[0];
        const ci = String(tipoPersona || '').toLowerCase() === 'juridica' ? null : base;
        return { tipo_documento_id: id, ci, ruc: numero, nro_documento: numero };
    }
    return { tipo_documento_id: id, ci: null, ruc: null, nro_documento: numero };
};

/**
 * documentoDesdeCliente — RN-FC11: arma tipo y número a partir de lo que tiene
 * un cliente guardado con el formato viejo o el nuevo.
 */
export const documentoDesdeCliente = (fila = {}) => {
    const porId = TIPO_POR_ID[Number(fila.tipo_documento_id)];
    if (porId && !['CI', 'RUC'].includes(porId)) {
        return { tipo: porId, numero: limpiar(fila.nro_documento).toUpperCase(), ruc: null };
    }
    if (porId === 'CI' && limpiar(fila.ci)) {
        const ruc = validarRuc(fila.ruc);
        return { tipo: 'CI', numero: limpiar(fila.ci), ruc: ruc.ok && fila.ruc ? ruc.ruc : null };
    }
    for (const candidato of [fila.ruc, fila.nro_documento]) {
        const r = limpiar(candidato);
        if (/^\d{3,9}-\d$/.test(r) && validarRuc(r).ok) return { tipo: 'RUC', numero: r, ruc: r };
    }
    const ci = limpiar(fila.ci) || limpiar(fila.nro_documento);
    if (/^\d{4,9}$/.test(ci)) return { tipo: 'CI', numero: ci, ruc: null };
    return { tipo: 'CI', numero: '', ruc: null };
};

// RN-FC7: `+` opcional y de 7 a 15 dígitos (15 es el límite de FE).
export const validarCelular = (valor) => {
    const v = String(valor ?? '').trim();
    if (!v) return { ok: true, valor: null };
    if (!/^\+?\d{7,15}$/.test(v)) return { ok: false, error: 'El celular va sólo con números (y un + adelante si es internacional), sin espacios, puntos ni guiones; entre 7 y 15 dígitos.' };
    return { ok: true, valor: v };
};

// RN-FC8.
export const CORREO_MAX = 100;
export const validarCorreo = (valor) => {
    const v = String(valor ?? '').trim();
    if (!v) return { ok: true, valor: null };
    if (v.length > CORREO_MAX || !/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(v)) {
        return { ok: false, error: 'El correo no es válido (por ejemplo, nombre@dominio.com).' };
    }
    return { ok: true, valor: v.toLowerCase() };
};
